import { useContext, useEffect, useState } from "react";
import { Outlet, useLocation, useMatch, useResolvedPath } from "react-router-dom";
import { DateTime } from "luxon";

import { Context } from "../../context/context";
import useWindowSize from "../../hooks/useWindowSize";
import Header from "../header/header.component";
import SideNav from "../side-nav/side-nav.component";
import { PageSpinner } from "../ui";
import MobileTabBar from "./mobile-tab-bar.component";
import { pageTitle } from "./nav-items";

const NAV_BREAKPOINT = 719;

const PAGE_TITLES = {
  "/": { title: "My Devices", subtitle: "Choose a device to manage" },
  "/profile": { title: "Profile", subtitle: "Your account details" },
  "/wifi-setup": { title: "Set up WiFi", subtitle: null },
};

const PageLayout = () => {
  const { state, dispatch } = useContext(Context);
  const { user } = state;

  const [drawerOpen, setDrawerOpen] = useState(false);

  const resolved = useResolvedPath("/device/:deviceId/:page");
  const match = useMatch({ path: resolved.pathname, end: true });
  const location = useLocation();
  const size = useWindowSize();

  const isDesktop = size.width > NAV_BREAKPOINT;
  const deviceId = match?.params.deviceId;
  const currentPage = match?.params.page;
  const nicknames = user?.nicknames || {};

  // Close the drawer when the viewport grows past the breakpoint.
  useEffect(() => {
    if (isDesktop) setDrawerOpen(false);
  }, [isDesktop]);

  // Reset the header's shadow state on navigation.
  useEffect(() => {
    dispatch({ type: "SET_SCROLL_POSITION", payload: 0 });
  }, [location.pathname, dispatch]);

  const handleScroll = (e) => {
    dispatch({ type: "SET_SCROLL_POSITION", payload: e.target.scrollTop });
  };

  const heading = deviceId
    ? { title: pageTitle(currentPage), subtitle: nicknames[deviceId] || deviceId }
    : PAGE_TITLES[location.pathname] || { title: "MedpeR", subtitle: null };

  if (!user) return <PageSpinner />;

  return (
    <div className="flex h-screen overflow-hidden bg-canvas">
      {isDesktop && <SideNav />}

      {!isDesktop && drawerOpen && (
        <>
          <div
            className="fixed inset-0 z-40 bg-primary-900/45"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
          <div className="fixed inset-y-0 left-0 z-50 animate-drawerin">
            <SideNav showClose onNavigate={() => setDrawerOpen(false)} />
          </div>
        </>
      )}

      <div className="flex min-w-0 flex-1 flex-col">
        <div className="flex min-h-0 flex-1 flex-col overflow-y-auto" onScroll={handleScroll}>
          <Header
            title={heading.title}
            subtitle={
              heading.subtitle ||
              DateTime.now().toFormat("cccc, d LLLL yyyy")
            }
            showMenuButton={!isDesktop}
            onOpenMenu={() => setDrawerOpen(true)}
          />
          <main
            key={location.pathname}
            className="flex-1 animate-panelfade px-4 py-5 nav:px-8 nav:py-7"
            style={{ paddingBottom: !isDesktop && deviceId ? "5.5rem" : undefined }}
          >
            <Outlet />
          </main>
        </div>
      </div>

      {!isDesktop && deviceId && <MobileTabBar deviceId={deviceId} currentPage={currentPage} />}
    </div>
  );
};

export default PageLayout;
